import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import {
  Button, Typography, message, Card, Table, Tag, Space,
  Divider, Input, Select, Row, Col, Modal, Progress, Spin, Tooltip,
} from "antd";
import {
  SyncOutlined, SearchOutlined, ClearOutlined, CheckCircleFilled,
  FileExcelOutlined, UserOutlined, FilterOutlined,
  LeftOutlined, RightOutlined, DownloadOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { actualizarDatosTemporalRit } from "../../services/reporteRit/temporalRitService";
import {
  obtenerEncuestadosPaginado,
  obtenerEncuestadosPorMunicipio,
  obtenerEncuestadosPorDepartamento,
  obtenerMunicipios,
  obtenerDepartamentos,
  obtenerTotalEncuestados,
  obtenerTodosEncuestadosEnBloques,
} from "../../services/reporteRit/promotorEncuestadoService";

const { Title, Text } = Typography;
const { Option } = Select;

const PAGE_SIZE_OPTIONS = [30, 50, 100];
const DEFAULT_PAGE_SIZE = 30;

const MENSAJES_PROGRESO = [
  "Conectando con el servidor...",
  "Obteniendo registros del API RIT...",
  "Procesando información recibida...",
  "Limpiando datos anteriores...",
  "Insertando registros en lotes...",
  "Validando integridad de los datos...",
  "Guardando cambios en la base de datos...",
  "Finalizando sincronización...",
];

const nombreCompleto = (row) =>
  [row.titular_nombre1, row.titular_nombre2, row.titular_apellido1, row.titular_apellido2]
    .filter(Boolean).join(" ");

const nombrePromotor = (row) =>
  [row.promotor_nombres, row.promotor_apellidos].filter(Boolean).join(" ");

const ReporteRit = () => {
  const [loadingConsultar, setLoadingConsultar] = useState(false);
  const [data, setData]                         = useState([]);

  // ── Paginación por cursor ─────────────────────────────────────────────────
  const [cursors,     setCursors]     = useState([0]);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [hasMore,     setHasMore]     = useState(false);
  const [nextCursor,  setNextCursor]  = useState(null);
  const [pageSize,    setPageSize]    = useState(DEFAULT_PAGE_SIZE);
  // modoFiltro: "paginado" | "municipio" | "departamento"
  const [modoFiltro,  setModoFiltro]  = useState("paginado");

  // ── Estado exclusivo para admin ───────────────────────────────────────────
  const [municipios,           setMunicipios]           = useState([]);
  const [municipioAdmin,       setMunicipioAdmin]       = useState(null);
  const [departamentos,        setDepartamentos]        = useState([]);
  const [departamentoAdmin,    setDepartamentoAdmin]    = useState(null);
  const [loadingMunicipios,    setLoadingMunicipios]    = useState(false);
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [totalRegistros,       setTotalRegistros]       = useState(0);

  // ── Descarga Excel ────────────────────────────────────────────────────────
  const [descargando,      setDescargando]      = useState(false);
  const [progresoDescarga, setProgresoDescarga] = useState(0);
  const [filasDescargadas, setFilasDescargadas] = useState(0);

  // ── Filtros de tabla ──────────────────────────────────────────────────────
  const [filtroDocumento, setFiltroDocumento] = useState("");
  const [filtroPromotor,  setFiltroPromotor]  = useState(null);
  const [filtroEstado,    setFiltroEstado]    = useState(null);

  // ── Modal de sincronización ───────────────────────────────────────────────
  const [modalVisible,             setModalVisible]             = useState(false);
  const [progreso,                 setProgreso]                 = useState(0);
  const [mensajeActual,            setMensajeActual]            = useState(MENSAJES_PROGRESO[0]);
  const [sincronizacionFinalizada, setSincronizacionFinalizada] = useState(false);
  const [totalSincronizado,        setTotalSincronizado]        = useState(0);
  const intervalRef   = useRef(null);
  const mensajeIdxRef = useRef(0);

  const usernameFromState = useSelector((state) => state.auth.user);
  const appName           = "reporteRIT";
  const permissions       = useSelector((state) => selectAppPermissions(state, appName));
  const permissionUpdate  = permissions.priv_update === "Y";
  const esAdmin           = usernameFromState?.role === "1";

  // ── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (esAdmin) {
      cargarMunicipios();
      cargarDepartamentos();
      cargarPaginado(0, DEFAULT_PAGE_SIZE, true);
      obtenerTotalEncuestados().then(setTotalRegistros).catch(console.error);
    } else {
      cargarTablaMunicipio();
    }
  }, []);

  // ── Mapeo de filas para la tabla ──────────────────────────────────────────
  const mapearFilas = (items) =>
    items.map((item) => ({
      key:              item.id_temporal_rit ?? item.id_encuesta ?? `${item.promotor_id}-${item.documento_titular}`,
      promotorId:       item.promotor_id,
      promotor:         nombrePromotor(item),
      municipioId:      item.municipio_id,
      municipio:        item.municipio    || "—",
      departamentoId:   item.departamento_id,
      departamento:     item.departamento || "—",
      documentoTitular: item.documento_titular     || "—",
      titular:          nombreCompleto(item)        || "—",
      direccion:        item.direccion_participante || "—",
      idEncuesta:       item.id_encuesta,
      fechaCarga:       item.fecha_carga
        ? new Date(item.fecha_carga).toLocaleString("es-CO") : "—",
      estado: item.estado,
      _raw:   item,
    }));

  // ── Mapeo de filas para exportación (desde raw del backend) ──────────────
  const mapearFilasExport = (items) =>
    items.map((item) => ({
      key:              item.id_temporal_rit,
      idEncuesta:       item.id_encuesta,
      documentoTitular: item.documento_titular     || "—",
      titular:          nombreCompleto(item)        || "—",
      direccion:        item.direccion_participante || "—",
      municipio:        item.municipio    || "—",
      departamento:     item.departamento || "—",
      promotor:         nombrePromotor(item),
      fechaCarga:       item.fecha_carga
        ? new Date(item.fecha_carga).toLocaleString("es-CO") : "—",
      estado: item.estado,
    }));

  // ── Carga paginada por cursor ─────────────────────────────────────────────
  const cargarPaginado = useCallback(async (
    lastId     = 0,
    size       = DEFAULT_PAGE_SIZE,
    reiniciar  = false,
    silencioso = false,
  ) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      const resultado = await obtenerEncuestadosPaginado(lastId, size);
      const filas = mapearFilas(resultado.data);
      setData(filas);
      setHasMore(resultado.hasMore);
      setNextCursor(resultado.nextCursor);
      setModoFiltro("paginado");
      limpiarFiltros();
      if (!silencioso) {
        filas.length > 0
          ? message.success(`${filas.length} registros cargados`)
          : message.warning("No se encontraron registros");
      }
      if (reiniciar) {
        setCursors([0]);
        setCursorIndex(0);
      }
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar los datos del RIT");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, []);

  // ── Siguiente / anterior página ───────────────────────────────────────────
  const irSiguientePagina = () => {
    if (!hasMore || nextCursor === null) return;
    const nuevosCursors = [...cursors.slice(0, cursorIndex + 1), nextCursor];
    setCursors(nuevosCursors);
    const nuevoIndex = cursorIndex + 1;
    setCursorIndex(nuevoIndex);
    cargarPaginado(nextCursor, pageSize);
  };

  const irPaginaAnterior = () => {
    if (cursorIndex === 0) return;
    const nuevoIndex = cursorIndex - 1;
    setCursorIndex(nuevoIndex);
    cargarPaginado(cursors[nuevoIndex], pageSize);
  };

  const handlePageSizeChange = (nuevoSize) => {
    setPageSize(nuevoSize);
    cargarPaginado(0, nuevoSize, true);
  };

  // ── Cargar por municipio / departamento ───────────────────────────────────
  const cargarTablaMunicipio = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      const resultado = await obtenerEncuestadosPorMunicipio(usernameFromState.cod_municipio);
      if (!resultado?.success) return;
      setData(mapearFilas(resultado.data));
      setModoFiltro("municipio");
      setHasMore(false);
      limpiarFiltros();
      if (!silencioso) {
        resultado.data.length > 0
          ? message.success(`${resultado.data.length.toLocaleString("es-CO")} registros cargados`)
          : message.warning("No se encontraron registros");
      }
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar los datos del RIT");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, [usernameFromState]);

  const cargarTablaAdminMunicipio = useCallback(async (municipioId, silencioso = false) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      if (municipioId) {
        const resultado = await obtenerEncuestadosPorMunicipio(municipioId);
        if (!resultado?.success) return;
        setData(mapearFilas(resultado.data));
        setModoFiltro("municipio");
        setHasMore(false);
        limpiarFiltros();
        if (!silencioso)
          message.success(`${resultado.data.length.toLocaleString("es-CO")} registros del municipio`);
      } else {
        await cargarPaginado(0, pageSize, true, silencioso);
      }
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar los datos del RIT");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, [pageSize, cargarPaginado]);

  const cargarTablaAdminDepartamento = useCallback(async (departamentoId, silencioso = false) => {
    if (!silencioso) setLoadingConsultar(true);
    try {
      if (departamentoId) {
        const resultado = await obtenerEncuestadosPorDepartamento(departamentoId);
        if (!resultado?.success) return;
        setData(mapearFilas(resultado.data));
        setModoFiltro("departamento");
        setHasMore(false);
        limpiarFiltros();
        if (!silencioso)
          message.success(`${resultado.data.length.toLocaleString("es-CO")} registros del departamento`);
      } else {
        await cargarPaginado(0, pageSize, true, silencioso);
      }
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar los datos del RIT");
    } finally {
      if (!silencioso) setLoadingConsultar(false);
    }
  }, [pageSize, cargarPaginado]);

  // ── Catálogos ─────────────────────────────────────────────────────────────
  const cargarMunicipios = useCallback(async () => {
    setLoadingMunicipios(true);
    try {
      const resultado = await obtenerMunicipios();
      if (resultado?.success) setMunicipios(resultado.data);
    } catch { message.error("Error al cargar la lista de municipios"); }
    finally { setLoadingMunicipios(false); }
  }, []);

  const cargarDepartamentos = useCallback(async () => {
    setLoadingDepartamentos(true);
    try {
      const resultado = await obtenerDepartamentos();
      if (resultado?.success) setDepartamentos(resultado.data);
    } catch { message.error("Error al cargar la lista de departamentos"); }
    finally { setLoadingDepartamentos(false); }
  }, []);

  // ── Handlers selectores admin ─────────────────────────────────────────────
  const handleMunicipioAdminChange = (value) => {
    setMunicipioAdmin(value ?? null);
    setDepartamentoAdmin(null);
    cargarTablaAdminMunicipio(value ?? null);
  };

  const handleDepartamentoAdminChange = (value) => {
    setDepartamentoAdmin(value ?? null);
    setMunicipioAdmin(null);
    cargarTablaAdminDepartamento(value ?? null);
  };

  // ── Sincronización ────────────────────────────────────────────────────────
  const iniciarProgreso = () => {
    setProgreso(0);
    setSincronizacionFinalizada(false);
    setMensajeActual(MENSAJES_PROGRESO[0]);
    mensajeIdxRef.current = 0;
    setModalVisible(true);
    intervalRef.current = setInterval(() => {
      setProgreso((prev) => {
        if (prev >= 85) { clearInterval(intervalRef.current); return prev; }
        const siguiente = prev + Math.random() * 2 + 0.5;
        const nuevoIdx  = Math.min(Math.floor(siguiente / 12), MENSAJES_PROGRESO.length - 1);
        if (nuevoIdx !== mensajeIdxRef.current) {
          mensajeIdxRef.current = nuevoIdx;
          setMensajeActual(MENSAJES_PROGRESO[nuevoIdx]);
        }
        return Math.min(siguiente, 85);
      });
    }, 600);
  };

  const finalizarProgreso = useCallback((total) => {
    clearInterval(intervalRef.current);
    setMensajeActual("¡Sincronización completada!");
    setTotalSincronizado(total);
    let p = 85;
    const finish = setInterval(() => {
      p += 3;
      setProgreso(Math.min(p, 100));
      if (p >= 100) { clearInterval(finish); setSincronizacionFinalizada(true); }
    }, 60);
  }, []);

  const handleActualizar = async () => {
    iniciarProgreso();
    try {
      const resp = await actualizarDatosTemporalRit();
      finalizarProgreso(resp.total_registros ?? resp.total ?? 0);
      if (esAdmin) {
        if (departamentoAdmin)   await cargarTablaAdminDepartamento(departamentoAdmin, true);
        else if (municipioAdmin) await cargarTablaAdminMunicipio(municipioAdmin, true);
        else                     await cargarPaginado(cursors[cursorIndex], pageSize, false, true);
      } else {
        await cargarTablaMunicipio(true);
      }
    } catch (error) {
      console.error(error);
      clearInterval(intervalRef.current);
      setModalVisible(false);
      message.error("Error al sincronizar los datos del RIT");
    }
  };

  const cerrarModal = () => {
    setModalVisible(false);
    setProgreso(0);
    setSincronizacionFinalizada(false);
  };

  // ── Filtros ───────────────────────────────────────────────────────────────
  const limpiarFiltros = () => {
    setFiltroDocumento("");
    setFiltroPromotor(null);
    setFiltroEstado(null);
  };

  const promotoresDisponibles = useMemo(() => {
    const set = new Set(data.map((r) => r.promotor).filter((p) => p && p !== "—"));
    return [...set].map((n) => ({ value: n, label: n }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const dataFiltrada = useMemo(() => data.filter((row) => {
    const coincideDocumento = !filtroDocumento || row.documentoTitular.includes(filtroDocumento);
    const coincidePromotor  = !filtroPromotor  || row.promotor === filtroPromotor;
    const coincideEstado    = filtroEstado == null ? true : row.estado === filtroEstado;
    return coincideDocumento && coincidePromotor && coincideEstado;
  }), [data, filtroDocumento, filtroPromotor, filtroEstado]);

  const hayFiltrosActivos = filtroDocumento || filtroPromotor || filtroEstado !== null;

  // ── Generación del archivo Excel ──────────────────────────────────────────
  const generarExcel = (filas) => {
    if (filas.length === 0) { message.warning("No hay datos para exportar"); return; }
    const exportData = filas.map((row) => ({
      "ID Encuesta":       row.idEncuesta,
      "Documento Titular": row.documentoTitular,
      "Titular":           row.titular,
      "Dirección":         row.direccion,
      "Municipio":         row.municipio,
      "Departamento":      row.departamento,
      "Encuestador":       row.promotor,
      "Fecha Carga":       row.fechaCarga,
      "Estado":            row.estado === 1 ? "Activo" : "Inactivo",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    worksheet["!cols"] = Object.keys(exportData[0]).map((key) => ({
      wch: Math.max(key.length, ...exportData.map((r) => String(r[key] ?? "").length)) + 2,
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte RIT");
    XLSX.writeFile(workbook, `ReporteRIT_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Descarga Excel ────────────────────────────────────────────────────────
  const descargarExcel = async () => {
    // Caso 1: no-admin o admin con filtro muni/depto → exporta lo que hay en tabla
    const exportarParcial = !esAdmin || modoFiltro !== "paginado";
    if (exportarParcial) {
      if (dataFiltrada.length === 0) { message.warning("No hay datos para exportar"); return; }
      generarExcel(dataFiltrada);
      message.success(`Excel exportado con ${dataFiltrada.length.toLocaleString("es-CO")} registros`);
      return;
    }

    // Caso 2: admin modo paginado → descarga TODO por bloques de 500
    setDescargando(true);
    setProgresoDescarga(0);
    setFilasDescargadas(0);

    try {
      const todos = await obtenerTodosEncuestadosEnBloques(
        (pct, cargadas) => {
          setProgresoDescarga(pct ?? 0);
          setFilasDescargadas(cargadas);
        },
        totalRegistros,
        500,
      );

      // Mapear los raw del backend al formato de exportación
      const filasMapeadas = mapearFilasExport(todos);

      // Aplicar filtros de tabla si están activos sobre TODA la data
      const filasFiltradas = hayFiltrosActivos
        ? filasMapeadas.filter((row) => {
            const coincideDocumento = !filtroDocumento || row.documentoTitular.includes(filtroDocumento);
            const coincidePromotor  = !filtroPromotor  || row.promotor === filtroPromotor;
            const coincideEstado    = filtroEstado == null ? true : row.estado === filtroEstado;
            return coincideDocumento && coincidePromotor && coincideEstado;
          })
        : filasMapeadas;

      setProgresoDescarga(100);

      if (hayFiltrosActivos) {
        message.info(
          `Filtros aplicados: exportando ${filasFiltradas.length.toLocaleString("es-CO")} ` +
          `de ${filasMapeadas.length.toLocaleString("es-CO")} registros`
        );
      } else {
        message.success(`Excel generado con ${filasFiltradas.length.toLocaleString("es-CO")} registros`);
      }

      generarExcel(filasFiltradas);

    } catch (error) {
      console.error(error);
      message.error("Error al descargar los datos para Excel");
    } finally {
      setDescargando(false);
      setProgresoDescarga(0);
      setFilasDescargadas(0);
    }
  };

  // ── Columnas ──────────────────────────────────────────────────────────────
  const columns = [
    { title: "ID Encuesta",  dataIndex: "idEncuesta",       key: "idEncuesta",       width: 120, align: "center" },
    { title: "N° Documento", dataIndex: "documentoTitular", key: "documentoTitular", width: 140 },
    { title: "Titular",      dataIndex: "titular",          key: "titular",          ellipsis: true },
    { title: "Dirección",    dataIndex: "direccion",        key: "direccion",        ellipsis: true },
    { title: "Municipio",    dataIndex: "municipio",        key: "municipio",        width: 150 },
    { title: "Departamento", dataIndex: "departamento",     key: "departamento",     width: 150 },
    {
      title: "Encuestador", dataIndex: "promotor", key: "promotor", ellipsis: true,
      render: (val) => <Space size={4}><UserOutlined style={{ color: "#1677ff" }} />{val}</Space>,
    },
    { title: "Fecha Carga", dataIndex: "fechaCarga", key: "fechaCarga", width: 180 },
    {
      title: "Estado", dataIndex: "estado", key: "estado", width: 100, align: "center",
      render: (val) => val === 1
        ? <Tag color="success">Activo</Tag>
        : <Tag color="error">Inactivo</Tag>,
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Reporte RIT</Title>

      {/* ── Acciones principales ──────────────────────────────────────────── */}
      <Space style={{ marginBottom: 16 }}>
        {permissionUpdate && (
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleActualizar}
            disabled={modalVisible || descargando}
          >
            Actualizar datos
          </Button>
        )}

        <Tooltip
          title={
            descargando
              ? `Descargando... ${filasDescargadas.toLocaleString("es-CO")} de ${totalRegistros.toLocaleString("es-CO")} registros`
              : esAdmin && modoFiltro === "paginado"
              ? `Exportar todos los registros (${totalRegistros.toLocaleString("es-CO")})`
              : dataFiltrada.length === 0
              ? "No hay datos para exportar"
              : `Exportar ${dataFiltrada.length.toLocaleString("es-CO")} registros`
          }
        >
          <Button
            icon={descargando ? <DownloadOutlined /> : <FileExcelOutlined />}
            onClick={descargarExcel}
            loading={descargando}
            disabled={descargando || (!esAdmin && dataFiltrada.length === 0)}
            style={{ borderColor: "#217346", color: descargando ? undefined : "#217346" }}
          >
            {descargando ? `Descargando ${progresoDescarga}%` : "Descargar Excel"}
          </Button>
        </Tooltip>
      </Space>

      {/* ── Barra de progreso de descarga ─────────────────────────────────── */}
      {descargando && (
        <div style={{ marginBottom: 16, maxWidth: 500 }}>
          <Progress
            percent={progresoDescarga}
            status="active"
            strokeColor={{ "0%": "#217346", "100%": "#52c41a" }}
            format={() => (
              <Text style={{ fontSize: 12 }}>
                {filasDescargadas.toLocaleString("es-CO")} / {totalRegistros.toLocaleString("es-CO")}
              </Text>
            )}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Preparando archivo Excel, no cierres esta ventana...
          </Text>
        </div>
      )}

      {/* ── Selectores municipio / departamento — solo admin ─────────────── */}
      {esAdmin && (
        <>
          <Divider orientation="left" style={{ margin: "12px 0" }}>
            <Space><FilterOutlined /> Filtrar por municipio o departamento</Space>
          </Divider>

          <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filtrar por municipio..."
                style={{ width: "100%" }}
                value={municipioAdmin}
                onChange={handleMunicipioAdminChange}
                loading={loadingMunicipios}
                allowClear showSearch optionFilterProp="label"
                options={municipios.map((m) => ({ value: m.municipio_id, label: m.municipio }))}
                suffixIcon={<FilterOutlined />}
                disabled={descargando}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filtrar por departamento..."
                style={{ width: "100%" }}
                value={departamentoAdmin}
                onChange={handleDepartamentoAdminChange}
                loading={loadingDepartamentos}
                allowClear showSearch optionFilterProp="label"
                options={departamentos.map((d) => ({ value: d.departamento_id, label: d.departamento }))}
                suffixIcon={<FilterOutlined />}
                disabled={descargando}
              />
            </Col>
            {(municipioAdmin || departamentoAdmin) && (
              <Col>
                <Text type="secondary">
                  Mostrando todos los registros del{" "}
                  {municipioAdmin ? "municipio" : "departamento"} seleccionado
                </Text>
              </Col>
            )}
          </Row>
        </>
      )}

      {/* ── Modal de sincronización ───────────────────────────────────────── */}
      <Modal
        open={modalVisible}
        footer={sincronizacionFinalizada
          ? <Button type="primary" onClick={cerrarModal}>Cerrar</Button>
          : null}
        closable={false} maskClosable={false} centered width={460}
        title={
          <Space>
            {sincronizacionFinalizada
              ? <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
              : <SyncOutlined spin style={{ color: "#1677ff", fontSize: 18 }} />}
            <span>
              {sincronizacionFinalizada ? "Sincronización completada" : "Sincronizando datos RIT"}
            </span>
          </Space>
        }
      >
        <div style={{ padding: "16px 0" }}>
          <Progress
            percent={Math.round(progreso)}
            status={sincronizacionFinalizada ? "success" : "active"}
            strokeColor={sincronizacionFinalizada ? "#52c41a" : { "0%": "#1677ff", "100%": "#36cfc9" }}
          />
          <div style={{ marginTop: 12, minHeight: 44 }}>
            {sincronizacionFinalizada ? (
              <Space direction="vertical" size={2}>
                <Text strong style={{ color: "#52c41a" }}>{mensajeActual}</Text>
                <Text type="secondary">
                  Se sincronizaron{" "}
                  <Text strong>{totalSincronizado.toLocaleString("es-CO")}</Text>{" "}
                  registros. La tabla ha sido actualizada.
                </Text>
              </Space>
            ) : (
              <Text type="secondary">{mensajeActual}</Text>
            )}
          </div>
        </div>
      </Modal>

      {/* ── Tabla ─────────────────────────────────────────────────────────── */}
      <Spin spinning={loadingConsultar} tip="Cargando datos..." size="large">
        <div style={{ minHeight: loadingConsultar ? 400 : "auto" }}>

          {!loadingConsultar && data.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <img
                src="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                alt="sin datos"
                style={{ width: 120, marginBottom: 16, opacity: 0.45 }}
              />
              <Text style={{ fontSize: 16, color: "#8c8c8c" }}>
                {esAdmin
                  ? "No se encontraron registros"
                  : "No se encontraron registros en la base de datos"}
              </Text>
            </div>
          )}

          {data.length > 0 && !loadingConsultar && (
            <>
              <Divider orientation="left" style={{ margin: "12px 0" }}>
                Filtros de tabla
              </Divider>

              <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="Buscar por N° documento"
                    prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                    value={filtroDocumento}
                    onChange={(e) => setFiltroDocumento(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Filtrar por encuestador"
                    style={{ width: "100%" }}
                    value={filtroPromotor}
                    onChange={setFiltroPromotor}
                    allowClear showSearch optionFilterProp="label"
                    options={promotoresDisponibles}
                  />
                </Col>
                <Col xs={24} sm={12} md={4} lg={3}>
                  <Select
                    placeholder="Estado"
                    style={{ width: "100%" }}
                    value={filtroEstado}
                    onChange={setFiltroEstado}
                    allowClear
                  >
                    <Option value={1}>Activo</Option>
                    <Option value={0}>Inactivo</Option>
                  </Select>
                </Col>
                {hayFiltrosActivos && (
                  <Col>
                    <Button icon={<ClearOutlined />} onClick={limpiarFiltros}>
                      Limpiar
                    </Button>
                  </Col>
                )}
              </Row>

              {/* ── Info + controles de paginación ─────────────────────────── */}
              <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                <Col>
                  <Text type="secondary">
                    {modoFiltro === "paginado" ? (
                      <>
                        Mostrando{" "}
                        <Text strong>
                          {cursorIndex * pageSize + 1}–{Math.min((cursorIndex + 1) * pageSize, totalRegistros)}
                        </Text>{" "}
                        de <Text strong>{totalRegistros.toLocaleString("es-CO")}</Text> registros
                        {" "}· página <Text strong>{cursorIndex + 1}</Text>
                        {hayFiltrosActivos && <Text type="warning"> (con filtros aplicados)</Text>}
                      </>
                    ) : (
                      <>
                        Mostrando{" "}
                        <Text strong>{dataFiltrada.length.toLocaleString("es-CO")}</Text>{" "}
                        registro{dataFiltrada.length !== 1 ? "s" : ""}
                        {hayFiltrosActivos && <Text type="warning"> (con filtros aplicados)</Text>}
                      </>
                    )}
                  </Text>
                </Col>

                {/* Botones anterior/siguiente solo en modo paginado admin */}
                {esAdmin && modoFiltro === "paginado" && (
                  <Col>
                    <Space>
                      <Space size={4}>
                        <Text type="secondary" style={{ fontSize: 13 }}>Filas:</Text>
                        <Select
                          size="small"
                          value={pageSize}
                          onChange={handlePageSizeChange}
                          style={{ width: 70 }}
                          disabled={descargando}
                          options={PAGE_SIZE_OPTIONS.map((s) => ({ value: s, label: s }))}
                        />
                      </Space>
                      <Button
                        size="small"
                        icon={<LeftOutlined />}
                        onClick={irPaginaAnterior}
                        disabled={cursorIndex === 0 || loadingConsultar || descargando}
                      >
                        Anterior
                      </Button>
                      <Button
                        size="small"
                        icon={<RightOutlined />}
                        iconPosition="end"
                        onClick={irSiguientePagina}
                        disabled={!hasMore || loadingConsultar || descargando}
                      >
                        Siguiente
                      </Button>
                    </Space>
                  </Col>
                )}
              </Row>

              <Table
                columns={columns}
                dataSource={dataFiltrada}
                pagination={
                  modoFiltro === "paginado"
                    ? false
                    : {
                        pageSize: 30,
                        showSizeChanger: true,
                        pageSizeOptions: ["30", "50", "100"],
                        showTotal: (total, range) =>
                          `${range[0]}–${range[1]} de ${total} registros`,
                      }
                }
                scroll={{ x: 1300 }}
                size="middle"
                bordered
              />
            </>
          )}
        </div>
      </Spin>
    </Card>
  );
};

export default ReporteRit;
